import React from 'react';
import { useAuth } from '../context/AuthContext';
import { LogOut, Menu, X, Shield, User as UserIcon } from 'lucide-react';

interface HeaderProps {
  onToggleSidebar: () => void;
  sidebarOpen: boolean;
}

export const Header: React.FC<HeaderProps> = ({ onToggleSidebar, sidebarOpen }) => {
  const { user, logout } = useAuth();

  const getRoleLabel = () => {
    if (!user) return '';
    if (user.role === 'deo' || user.role === 'admin') return 'District Education Officer (DEO)';
    if (user.role === 'beo') return `Block Education Officer (BEO - ${user.block || 'Nuh'})`;
    if (user.role === 'school' || user.role === 'principal') return 'School Head / Principal';
    return 'Subject Teacher';
  };

  return (
    <header className="bg-[#0F2C59] text-white shadow-md sticky top-0 z-40 border-b-2 border-[#D97706]">
      <div className="px-4 py-2.5 flex items-center justify-between relative">
        {/* Left: Mobile Toggle & Emblem */}
        <div className="flex items-center space-x-2 z-10">
          <button
            onClick={onToggleSidebar}
            className="p-1.5 rounded-md hover:bg-white/10 lg:hidden text-slate-200 focus:outline-none"
            aria-label="Toggle menu"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          
          <div className="w-9 h-9 rounded bg-amber-500/20 border border-amber-400/50 hidden sm:flex items-center justify-center text-amber-400 font-bold text-xs shadow-inner">
            DSE
          </div>
        </div>

        {/* Center: Strict 3-Line Official Header */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 pointer-events-none">
          <h1 className="text-xs md:text-sm font-semibold text-slate-100 tracking-wide uppercase leading-tight">
            Department of School Education, Haryana
          </h1>
          <h2 className="text-xs md:text-sm font-bold text-amber-400 tracking-wide leading-tight">
            District Education Office, Nuh
          </h2>
          <h3 className="text-[11px] md:text-xs font-medium text-slate-200 leading-tight">
            Monthly Assessment and SAT Marks Monitoring Portal
          </h3>
        </div>

        {/* Right: User Information & Logout */}
        <div className="flex items-center space-x-2.5 z-10">
          {user && (
            <div className="hidden md:flex items-center space-x-2 bg-white/10 px-2.5 py-1 rounded border border-white/15 text-left">
              <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center text-slate-950 font-bold text-[10px]">
                {user.role === 'deo' || user.role === 'admin' ? 'D' : user.role === 'beo' ? 'B' : 'S'}
              </div>
              <div>
                <div className="text-[11px] font-semibold text-white leading-none">
                  {user.name}
                </div>
                <div className="text-[9px] text-amber-300 font-medium leading-none mt-0.5">
                  {getRoleLabel()}
                </div>
              </div>
            </div>
          )}

          <button
            onClick={logout}
            className="flex items-center space-x-1 bg-rose-700 hover:bg-rose-800 text-white text-[11px] font-medium px-2.5 py-1 rounded shadow transition border border-rose-600"
            title="Logout"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};
